import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { ChartSize, DrilldownTableColumn, DrilldownTableData, DrilldownTableRow } from '@models';
import { CommonFunctionsService } from '@services';

@Component({
  selector: 'drilldown-table',
  templateUrl: './drilldown-table.component.html',
  styleUrl: './drilldown-table.component.scss'
})
export class DrilldownTableComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  private commonFunctionsService: CommonFunctionsService = inject(CommonFunctionsService);
  private readonly hostElementRef = inject(ElementRef<HTMLElement>);
  private readonly spacerRowKey = '__spacerRow';
  private readonly headerRowHeightPx = 40;
  private readonly rowHeightPx = 40;
  private resizeObserver?: ResizeObserver;
  @ViewChild('tableScrollViewport') tableScrollViewport?: ElementRef<HTMLElement>;

  @Input() data!: DrilldownTableData;
  @Input() size!: ChartSize;
  dataSource = new MatTableDataSource<DrilldownTableRow>([]);
  displayedColumns: DrilldownTableColumn[] = [];
  displayedColumnsKeys: string[] = [];
  noDataAvailable = false;
  addSpacerRow = false;

  ngOnInit() {
    this.initializeTableState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['data'] && !changes['size']) {
      return;
    }

    this.initializeTableState();
  }

  ngAfterViewInit(): void {
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateSpacerForCurrentLayout();
      });
      this.resizeObserver.observe(this.hostElementRef.nativeElement);
      if (this.tableScrollViewport?.nativeElement) {
        this.resizeObserver.observe(this.tableScrollViewport.nativeElement);
      }
    }
    // Run once after layout settles to get the final rendered height.
    setTimeout(() => this.updateSpacerForCurrentLayout());
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  isSpacerRow(row: DrilldownTableRow | Record<string, unknown>): boolean {
    return !!(row as Record<string, unknown>)?.[this.spacerRowKey];
  }

  getSpacerRowHeight(): number {
    if (this.size === 'small') {
      return 0;
    }

    const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 900;
    if (viewportHeight <= 900) {
      return 12;
    }
    if (viewportHeight <= 1080) {
      return 16;
    }
    return 20;
  }

  getColumnClass(column: DrilldownTableColumn): string | undefined {
    if (column.isBadge) {
      switch (this.size) {
        case 'small':
          return 'mat-column-badge-no-label';
        case 'medium':
        case 'large':
          return 'mat-column-badge-with-label';
      }
    }

    if (column.key == 'milestoneName') return 'mat-column-milestone-name';

    return;
  }

  formatLabel(column: DrilldownTableColumn, label: string): string | undefined {
    if (column.isDate) {
      return this.commonFunctionsService.formatUndefinedDate(label);
    }

    return label;
  }

  private setDisplayedColumns() {
    this.data.columns.forEach((column) => {
      if (column.alwaysVisible || column.visibleSizes?.includes(this.size)) {
        this.displayedColumns.push(column);
        this.displayedColumnsKeys.push(column.key);
      }
    });
  }

  private initializeTableState(): void {
    if (!this.data?.rows || this.data.rows.length === 0) {
      this.noDataAvailable = true;
      this.dataSource.data = [];
      this.displayedColumns = [];
      this.displayedColumnsKeys = [];
      return;
    }

    this.noDataAvailable = false;
    this.displayedColumns = [];
    this.displayedColumnsKeys = [];
    this.setDisplayedColumns();
    this.refreshTableRows();
  }

  private refreshTableRows(): void {
    if (!this.data?.rows) {
      this.dataSource.data = [];
      return;
    }
    this.dataSource.data = this.getRowsWithResponsiveSpacer(this.data.rows);
  }

  private updateSpacerForCurrentLayout(): void {
    if (!this.data?.rows?.length) {
      return;
    }

    const shouldAddSpacer = this.shouldAddSpacerRow();
    if (this.addSpacerRow !== shouldAddSpacer) {
      this.addSpacerRow = shouldAddSpacer;
      this.refreshTableRows();
    }
  }

  private shouldAddSpacerRow(): boolean {
    if (this.size !== 'medium' && this.size !== 'large') {
      return false;
    }

    const viewportHeight = this.getViewportHeight();
    if (viewportHeight <= 0) {
      return false;
    }

    const tableContentHeight = this.headerRowHeightPx + this.data.rows.length * this.rowHeightPx;

    // Add spacer only when rows overflow the visible table viewport.
    return tableContentHeight > viewportHeight;
  }

  private getViewportHeight(): number {
    const viewportElement = this.tableScrollViewport?.nativeElement;
    if (viewportElement) {
      const viewportRectHeight = viewportElement.getBoundingClientRect().height;
      if (viewportRectHeight > 0) {
        return viewportRectHeight;
      }
    }

    const hostHeight = this.hostElementRef.nativeElement.getBoundingClientRect().height;
    if (hostHeight <= 0) {
      return 0;
    }

    return hostHeight;
  }

  private getRowsWithResponsiveSpacer(rows: DrilldownTableRow[]): DrilldownTableRow[] {
    if (!this.addSpacerRow) {
      return rows;
    }

    // Keep one synthetic trailing row so the scroll area ends with breathing room.
    const spacerRow = { [this.spacerRowKey]: true } as unknown as DrilldownTableRow;
    return [...rows, spacerRow];
  }
}
